// @flow
import React, {useEffect, useMemo} from 'react';
import {makeStyles} from '@material-ui/styles';
import {Route, Switch, useLocation} from 'react-router-dom';

import {Chip, Typography} from '@material-ui/core';

import {usePage} from '../../page/utils';
import {colors} from '../../../assets/styles/colors';
import {useIsFeatureInTier} from '../../../utils/component';

import LoadingSpinner from '../../../elements/LoadingSpinner/LoadingSpinner';
import LinkButton from './components/LinkButton/LinkButton';

import HomePageContainer from '../../home-pages/containers/HomePages';
import ProductPageConfigurationContainer from '../product-page/containers/ProductPageConfigurationContainer';
import ProductFiltersContainer from '../../product-filters/containers/ProductFiltersContainer';

import TermsAndConditionsContainer from '../../configure/legal/containers/TermsAndConditionsContainer';
import AboutUsContainer from '../../configure/legal/containers/AboutUsContainer';
import ShippingsContainer from '../../configure/legal/containers/ShippingsContainer';
import FaqContainer from '../../configure/legal/containers/FaqContainer';
import RefundsContainer from '../../configure/legal/containers/RefundsContainer';
import ComplaintsContainer from '../../configure/legal/containers/ComplaintsContainer';

import type {User} from '../../../entities';
import type {LegalSetting} from '../../configure/legal/duck';
import {useTypedSelector} from '../../../store';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import PoliciesAndPrivacy from '../../configure/legal/policies-and-privacy';
import {useStoreMetadata, useStoreBusinessModel} from '../../store/utils';
import CustomChip from '../../../elements/CustomChip';
import {useCustomerComplaintSettings} from '../../customer-complaints/utils/useCustomerComplaintSetting';

type Props = {
  user?: User,
  saveAboutUsSuccess?: boolean,
  fetchStoreLegalSetting: () => void,
  resetState: () => void,
  saveStoreLegalSetting: ({legalSetting: {[key: string]: boolean}}) => void,
  legalSetting?: LegalSetting,
  isSavingLegalSetting?: boolean,
  saveFaqSuccess?: boolean,
  isFetching?: boolean,
  saveLegalSettingSuccess?: boolean,
  saveTermsSuccess?: boolean,
  saveRefundsSuccess?: boolean,
  saveShippingsSuccess?: boolean,
  saveLegalSettingError?: string,
};

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(2, 0),
    background: colors.grey200,
  },
  fullWidth: {
    width: '100%',
  },
  legalNameWrap: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  legalName: {
    fontSize: 16,
    color: colors.grey900,
    fontWeight: 600,
  },
  legalDesc: {
    fontSize: 14,
    fontWeight: 400,
    color: colors.grey900,
  },
  ul: {
    paddingLeft: '20px',
    '& li': {
      fontSize: 14,
      fontWeight: 400,
      color: colors.grey900,
      '& span': {
        color: colors.purple,
      },
    },
  },
  subrouteRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  btnAddPage: {
    color: colors.grey900,
    border: '1px solid #E8E8ED',
    padding: theme.spacing(1, 2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5, 1.5),
    },
  },
  handleEnableConfig: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  upsellMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: theme.spacing(2, 0),
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
      flexWrap: 'wrap',
    },
    color: colors.purple3,
    background: colors.lightPurple,
    borderRadius: '12px',
    fontSize: '0.875rem',
  },
  upgradeTierButton: {
    marginLeft: 'auto',
    backgroundColor: 'unset',
    color: colors.purple3,
    boxShadow: 'unset',
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(1),
    },
  },
  upgradeContainer: {
    position: 'absolute',
    top: 72,
    right: 0,
    zIndex: 100,
    width: '100%',
    height: '125vh',

    [theme.breakpoints.up('md')]: {
      height: 'calc(100vh - 72px)',
    },
  },

  sectionCard: {
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  sectionTitle: {
    fontSize: 12,
    letterSpacing: '1px',
    fontWeight: 500,
    color: colors.grey800,
  },
  innerTitle: {
    color: colors.grey900,
    fontSize: 16,
    fontWeight: 600,
  },
  innerSubtitle: {
    color: colors.grey900,
    fontSize: 14,
  },
  contentWrap: {
    marginTop: theme.spacing(2),
  },
  enabledChip: {
    background: 'rgba(0, 178, 130, 0.12)',
    marginLeft: 10,
    fontWeight: 600,
    '& span': {
      color: '#00805D',
    },
  },
  disabledChip: {
    background: colors.grey200,
    marginLeft: 10,
    fontWeight: 600,
    '& span': {
      color: colors.grey600,
    },
  },
  infoLink: {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 600,
  },
  customTitle: {
    display: 'flex',
    // flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '4px',
  },
  label: {
    fontWeight: 500,
    fontSize: 14,
    color: colors.grey900,
  },
  complaintsForm: {
    width: '100%',
    flexDirection: 'column',
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  complaintsFormSubmit: {
    maxWidth: '200px',
  },
}));

export default usePage<Props>(PagesConfiguration, 'Pages Configuration');

function PagesConfiguration(props: Props) {
  const {
    legalSetting,
    isFetching,
    isSavingLegalSetting,
    fetchStoreLegalSetting,
  } = props;

  const {setting} = useCustomerComplaintSettings();

  const location = useLocation();
  const classes = useStyles();
  const storeBusinessModel = useStoreBusinessModel();

  const hasStoreLegalFeature = useIsFeatureInTier('STORE_POLICIES');

  const storeMetadata = useStoreMetadata();

  const termsAndConditionsEnabled = storeMetadata
    ? storeMetadata?.featureRestrictions?.legalPage?.TERMS_AND_CONDITIONS
    : true;

  const saveLegalSettingSuccess = useTypedSelector(
    state => state.legalConfig.saveLegalSettingSuccess
  );

  useEffect(() => {
    if (hasStoreLegalFeature) {
      fetchStoreLegalSetting();
    }
  }, [fetchStoreLegalSetting, hasStoreLegalFeature]);

  const storePagesMap = {
    homePage: {
      name: 'Página de Inicio',
      description: 'Customiza la página principal de tu marca.',
      path: '/my-store/pages/home-page',
      component: HomePageContainer,
    },
    productPage: {
      name: 'Página de Producto',
      description:
        'Personaliza la página de producto y convierte mayores ventas.',
      path: '/my-store/pages/product-page',
      component: ProductPageConfigurationContainer,
      hide: storeBusinessModel !== 'PRODUCT',
    },
    filtersPage: {
      name: 'Filtros Y busqueda de producto ',
      description:
        'Personaliza las páginas de tus productos aplicando filtros de búsqueda.',
      path: '/my-store/pages/product-filters',
      component: ProductFiltersContainer,
      hide: storeBusinessModel !== 'PRODUCT',
    },
  };

  const legalRoutes = useMemo(() => {
    return {
      termsAndConditions: {
        enabled: legalSetting
          ? legalSetting.termsOfServiceInformationEnabled
          : null,
        name: 'Términos y condiciones',
        description:
          'Describe las reglas, los términos y las pautas legales que todos deben aceptar para poder acceder y hacer uso de su página web.',
        path: '/my-store/pages/legal/terms-and-conditions',
        component: TermsAndConditionsContainer,
        disabledEdit: !termsAndConditionsEnabled,
      },
      policiesAndPrivacy: {
        enabled: legalSetting ? legalSetting.policiesAndPrivacyEnabled : null,
        name: 'Políticas y privacidad',
        description:
          'Describe cómo recopila, utiliza y protege la información de sus clientes.',
        path: '/my-store/pages/legal/policies-and-privacy',
        component: PoliciesAndPrivacy,
        disabledEdit: !termsAndConditionsEnabled,
      },
      shippings: {
        enabled: legalSetting ? legalSetting.shippingInformationEnabled : null,
        name: 'Envíos y pagos',
        description:
          'Describa cómo envía/entrega sus productos, así tambien cómo acepta pagos por los pedidos.',
        path: '/my-store/pages/legal/shippings-and-payments',
        component: ShippingsContainer,
        disabledEdit: false,
      },
      refunds: {
        enabled: legalSetting ? legalSetting.refundInformationEnabled : null,
        name: 'Devoluciones',
        description:
          'Describa cómo realiza las devoluciones, cambios o reembolsos si los clientes no están satisfechos con sus pedidos. ',
        path: '/my-store/pages/legal/refunds',
        component: RefundsContainer,
        disabledEdit: false,
      },
      aboutUs: {
        enabled: legalSetting ? legalSetting.aboutInformationEnabled : null,
        name: 'Acerca de nosotros',
        description:
          'Proporcionar al cliente más información sobre su negocio y producto.',
        path: '/my-store/pages/legal/about-us',
        component: AboutUsContainer,
        disabledEdit: false,
      },
      faq: {
        enabled: legalSetting ? legalSetting.faqSettingEnabled : null,
        name: 'Preguntas Frecuentes',
        description:
          'Coloca respuestas a las preguntas frecuentes de tus clientes.',
        path: '/my-store/pages/legal/faq',
        component: FaqContainer,
        disabledEdit: false,
      },
      complaints: {
        enabled: !!setting?.enabled,
        name: 'Libro de reclamaciones',
        description:
          'Permite a los clientes enviar reclamos sobre productos o servicios de manera oficial.',
        path: '/my-store/pages/legal/complaints',
        component: ComplaintsContainer,
        disabledEdit: false,
      },
    };
  }, [legalSetting, setting, termsAndConditionsEnabled]);

  const storePagesRoutes = Object.keys(storePagesMap).map(
    k => storePagesMap[k]
  );

  const legalPageRoutes = Object.keys(legalRoutes).map(k => legalRoutes[k]);

  const isSubRoute = !![...storePagesRoutes, ...legalPageRoutes].find(el =>
    location.pathname.startsWith(el.path)
  );

  return (
    <>
      {saveLegalSettingSuccess && (
        <SnackBar mode="success" message="Guardado correctamente" />
      )}

      {(isFetching || isSavingLegalSetting) && <LoadingSpinner />}
      {!isSubRoute && (
        <div className={classes.root}>
          <section className={classes.sectionCard}>
            <Typography className={classes.sectionTitle}>
              PÁGINAS DE TIENDA
            </Typography>

            <div className={classes.contentWrap}>
              {storePagesRoutes
                .filter(el => !el.hide)
                .map(el => (
                  <LinkButton
                    key={el.name}
                    to={el.path}
                    title={
                      <div>
                        <Typography className={classes.innerTitle} gutterBottom>
                          {el.name}
                        </Typography>
                        <Typography className={classes.innerSubtitle}>
                          {el.description}
                        </Typography>
                      </div>
                    }
                  />
                ))}
            </div>
          </section>

          <section className={classes.sectionCard}>
            <Typography className={classes.sectionTitle}>
              PAGINAS ADICIONALES
            </Typography>

            <div className={classes.contentWrap}>
              {legalPageRoutes.map(el => (
                <LinkButton
                  key={el.name}
                  to={el.path}
                  disabled={el.disabledEdit}
                  title={
                    <div>
                      <div className={classes.customTitle}>
                        <Typography className={classes.innerTitle}>
                          {el.name}
                        </Typography>
                        {el.enabled ? (
                          <Chip
                            size="small"
                            className={classes.enabledChip}
                            label="ACTIVA"
                          />
                        ) : (
                          <Chip
                            size="small"
                            className={classes.disabledChip}
                            label="INACTIVA"
                          />
                        )}
                      </div>
                      <Typography className={classes.innerSubtitle}>
                        {el.description}
                      </Typography>

                      {el.disabledEdit && (
                        <>
                          <br />
                          <CustomChip
                            color="secondary"
                            size="small"
                            label="Esta página no puede ser editada."
                          />
                        </>
                      )}
                    </div>
                  }
                />
              ))}
            </div>
          </section>
        </div>
      )}

      <div className={classes.root}>
        <Switch>
          {[...storePagesRoutes, ...legalPageRoutes].map(
            (elem, idx: number) => (
              <Route
                key={`subroute-${elem.name}`}
                path={elem.path}
                component={elem.component}
              />
            )
          )}
        </Switch>
      </div>
    </>
  );
}
